/**
 * LiveSessionRoom — two-column live session workspace.
 *
 * Layout:
 *   ┌─ ToolSelector (global top nav, preserves site navigation) ────────┐
 *   ├─ Slim room bar: session title · Leave · Share · Raise hand ───────┤
 *   ├──────────────── Jitsi iframe ────────────┬── Roster / Notes tabs ─┤
 *   │                                          │  ─────────────────────  │
 *   │                                          │  <AdSlot                │
 *   │                                          │   location="live_room_ │
 *   │                                          │   sponsor" />           │
 *   └──────────────────────────────────────────┴─────────────────────────┘
 *
 * We intentionally do NOT take over the viewport — the site's global
 * ToolSelector remains visible so attendees can jump to Community, DMs,
 * Dashboard etc. without leaving the call abruptly. The Jitsi External API
 * (loaded from https://meet.jit.si/external_api.js) handles all A/V,
 * screenshare, breakouts, chat, whiteboard, and polls. We layer: roster
 * polling (5s), hand-raise persistence, and a co-editable shared-notes
 * pad that autosaves every 3s.
 */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import ToolSelector from '../components/ToolSelector';
import AdSlot from '../components/AdSlot';
import {
  Hand, Users, StickyNote, Loader2, ShieldCheck, ArrowLeft, Copy,
  Video as VideoIcon, StopCircle, Download,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

const JITSI_SCRIPT_ID = 'jitsi-external-api';
const NOTES_AUTOSAVE_MS = 3000;
const ROSTER_POLL_MS   = 5000;

const loadJitsiScript = (domain) => new Promise((resolve, reject) => {
  if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
  const existing = document.getElementById(JITSI_SCRIPT_ID);
  if (existing) {
    existing.addEventListener('load',  () => resolve(window.JitsiMeetExternalAPI));
    existing.addEventListener('error', reject);
    return;
  }
  const s = document.createElement('script');
  s.id  = JITSI_SCRIPT_ID;
  s.src = `https://${domain}/external_api.js`;
  s.async = true;
  s.onload  = () => resolve(window.JitsiMeetExternalAPI);
  s.onerror = () => reject(new Error('Failed to load Jitsi Meet'));
  document.head.appendChild(s);
});

const LiveSessionRoom = () => {
  const { API, user } = useContext(AuthContext);
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const apiRef       = useRef(null);
  const notesTimerRef = useRef(null);

  const [connecting, setConnecting] = useState(true);
  const [connectInfo, setConnectInfo] = useState(null);
  const [handUp, setHandUp] = useState(false);
  const [rightPanel, setRightPanel] = useState('roster');
  const [roster, setRoster] = useState({ live: [], past: [], live_count: 0, hands_up: 0 });
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesUpdatedAt, setNotesUpdatedAt] = useState(null);
  const [sessionMeta, setSessionMeta] = useState(null);

  // 1) Join server-side (creates attendance) + fetch session meta + notes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [joinRes, metaRes, notesRes] = await Promise.all([
          axios.post(`${API}/live-sessions/${sessionId}/join`),
          axios.get(`${API}/live-sessions/${sessionId}`),
          axios.get(`${API}/live-sessions/${sessionId}/notes`),
        ]);
        if (cancelled) return;
        setConnectInfo(joinRes.data);
        setSessionMeta(metaRes.data);
        setNotes(notesRes.data?.body_md || '');
        setNotesUpdatedAt(notesRes.data?.updated_at || null);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not join session');
        navigate('/live');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, API, navigate]);

  // 2) Mount Jitsi iframe once we have connectInfo.
  useEffect(() => {
    if (!connectInfo || !containerRef.current) return;
    let disposed = false;
    let apiLocal = null;
    (async () => {
      try {
        const Ctor = await loadJitsiScript(connectInfo.jitsi_domain);
        if (disposed) return;
        apiLocal = new Ctor(connectInfo.jitsi_domain, {
          roomName: connectInfo.jitsi_room_name,
          parentNode: containerRef.current,
          width: '100%',
          height: '100%',
          userInfo: { displayName: connectInfo.display_name || user?.name || 'Guest' },
          configOverwrite: {
            prejoinConfig: { enabled: false },
            disableDeepLinking: true,
            enableWelcomePage: false,
            enableClosePage: false,
            startWithAudioMuted: !connectInfo.is_host,
            startWithVideoMuted: !connectInfo.is_host,
          },
          interfaceConfigOverwrite: {
            DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
            MOBILE_APP_PROMO: false,
          },
        });
        apiRef.current = apiLocal;
        setConnecting(false);

        apiLocal.addEventListener('raiseHandUpdated', async (ev) => {
          const raised = !!(ev && ev.handRaised);
          setHandUp(raised);
          try {
            await axios.post(`${API}/live-sessions/${sessionId}/hand-raise`, { raised });
          } catch (_) { /* silent */ }
        });
        apiLocal.addEventListener('readyToClose', () => {
          leaveAndNavigate();
        });
      } catch (err) {
        toast.error('Could not start the video call — please refresh');
        setConnecting(false);
      }
    })();
    return () => {
      disposed = true;
      try { apiLocal && apiLocal.dispose(); } catch (_) { /* noop */ }
      apiRef.current = null;
      axios.post(`${API}/live-sessions/${sessionId}/leave`).catch(() => {});
    };
  }, [connectInfo, API, sessionId, user]);

  // 3) Poll roster every 5s.
  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/live-sessions/${sessionId}/roster`);
        setRoster(data);
      } catch (_) { /* silent */ }
    };
    poll();
    timer = setInterval(poll, ROSTER_POLL_MS);
    return () => clearInterval(timer);
  }, [API, sessionId]);

  // 4) Autosave notes 3s after last edit.
  const scheduleNotesSave = (value) => {
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(async () => {
      try {
        setNotesSaving(true);
        const { data } = await axios.put(`${API}/live-sessions/${sessionId}/notes`, { body_md: value });
        setNotesUpdatedAt(data?.updated_at);
      } catch (_) { /* silent */ }
      finally { setNotesSaving(false); }
    }, NOTES_AUTOSAVE_MS);
  };

  const leaveAndNavigate = async () => {
    try { apiRef.current && apiRef.current.dispose(); } catch (_) { /* noop */ }
    try { await axios.post(`${API}/live-sessions/${sessionId}/leave`); } catch (_) { /* silent */ }
    navigate('/live');
  };

  const toggleHand = () => {
    const nextRaised = !handUp;
    try {
      apiRef.current && apiRef.current.executeCommand('toggleRaiseHand');
    } catch (_) { /* noop */ }
    setHandUp(nextRaised);
    axios.post(`${API}/live-sessions/${sessionId}/hand-raise`, { raised: nextRaised })
      .catch(() => {});
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/live/${sessionId}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success('Share link copied'))
      .catch(() => toast.error('Could not copy'));
  };

  // -------- Browser-side recording (MediaRecorder + tab capture) ------------
  const recorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordStartRef = useRef(0);
  const [recording, setRecording] = useState(false);
  const [recordUploading, setRecordUploading] = useState(false);

  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 24 },
        audio: true,
      });
      // Merge mic audio if available so learners hear each other in the file.
      let combinedStream = stream;
      try {
        const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioTracks = [...stream.getAudioTracks(), ...mic.getAudioTracks()];
        combinedStream = new MediaStream([
          ...stream.getVideoTracks(),
          ...audioTracks,
        ]);
      } catch { /* no mic — screen audio only */ }
      const mimes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
      const mime = mimes.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || 'video/webm';
      const mr = new MediaRecorder(combinedStream, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
      recorderRef.current = mr;
      recordChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) recordChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(recordChunksRef.current, { type: mime });
        const duration = Math.round((Date.now() - recordStartRef.current) / 1000);
        // Stop stream tracks
        combinedStream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
        stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
        // Upload + persist metadata
        setRecordUploading(true);
        try {
          const form = new FormData();
          const filename = `live-${sessionId}-${Date.now()}.webm`;
          form.append('file', blob, filename);
          const { data: up } = await axios.post(`${API}/uploads/forum`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          const fileUrl = up?.url || up?.file_url;
          if (!fileUrl) throw new Error('Upload returned no URL');
          await axios.post(`${API}/live-sessions/${sessionId}/recordings`, {
            file_url:      fileUrl,
            title:         sessionMeta?.title
              ? `${sessionMeta.title} — recorded ${new Date().toLocaleString()}`
              : `Recording ${new Date().toLocaleString()}`,
            duration_sec:  duration,
            size_bytes:    blob.size,
            mime_type:     mime,
          });
          toast.success(`Recording saved (${Math.round(blob.size / 1024 / 1024)} MB, ${duration}s)`);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Recording upload failed');
        } finally { setRecordUploading(false); }
      };
      // Stop automatically when the user clicks the browser's "Stop sharing"
      stream.getVideoTracks()[0].onended = () => { try { mr.state !== 'inactive' && mr.stop(); } catch { /* ignore */ } };
      recordStartRef.current = Date.now();
      mr.start(1000); // gather chunks every second
      setRecording(true);
      toast.info('Recording started — you will be asked to pick the tab or window.');
    } catch (e) {
      toast.error(e?.name === 'NotAllowedError' ? 'Recording permission denied' : 'Could not start recording');
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    try { recorderRef.current && recorderRef.current.state !== 'inactive' && recorderRef.current.stop(); }
    catch { /* ignore */ }
    setRecording(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="live-session-room">
      {/* Global site nav — attendees never lose their way around the app */}
      <ToolSelector />

      {/* Slim room bar */}
      <div className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 py-2 flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={leaveAndNavigate}
            className="rounded-full h-8 text-xs font-bold"
            data-testid="leave-session-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Leave
          </Button>
          <div className="min-w-0 flex-1">
            <div className="font-heading text-sm md:text-base font-extrabold truncate">
              {sessionMeta?.title || 'Live session'}
            </div>
            {sessionMeta?.description && (
              <div className="text-xs text-muted-foreground truncate hidden sm:block">
                {sessionMeta.description}
              </div>
            )}
          </div>
          {connectInfo?.is_host && (
            <span className="inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
              <ShieldCheck className="w-3 h-3" /> Host
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={copyShareLink}
            className="rounded-full h-8 text-xs"
            data-testid="copy-share-btn"
          >
            <Copy className="w-3 h-3 mr-1" /> Share
          </Button>
          {/* Recording — host + admin only, gated by MediaRecorder support */}
          {(connectInfo?.is_host || user?.role === 'admin') && !!window.MediaRecorder && (
            recording ? (
              <Button
                size="sm"
                onClick={stopRecording}
                disabled={recordUploading}
                className="rounded-full h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white border-transparent animate-pulse"
                data-testid="stop-recording-btn"
              >
                <StopCircle className="w-3 h-3 mr-1" /> Stop
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={startRecording}
                disabled={recordUploading}
                className="rounded-full h-8 text-xs"
                data-testid="start-recording-btn"
              >
                {recordUploading
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving…</>
                  : <><VideoIcon className="w-3 h-3 mr-1" /> Record</>}
              </Button>
            )
          )}
          <Button
            size="sm"
            variant={handUp ? 'default' : 'outline'}
            onClick={toggleHand}
            className={`rounded-full h-8 text-xs ${handUp ? 'bg-amber-500 hover:bg-amber-600 text-white border-transparent' : ''}`}
            data-testid="hand-raise-btn"
          >
            <Hand className={`w-3 h-3 mr-1 ${handUp ? 'animate-bounce' : ''}`} />
            {handUp ? 'Hand up' : 'Raise hand'}
          </Button>
        </div>
      </div>

      {/* Body: Jitsi + right panel */}
      <div className="flex-1 max-w-[1600px] w-full mx-auto px-4 py-3 grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Left: Jitsi */}
        <div className="relative rounded-2xl overflow-hidden border border-border bg-black min-h-[520px] lg:min-h-[calc(100vh-180px)]">
          {connecting && (
            <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm z-10 bg-black/60">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Connecting to Jitsi Meet…
            </div>
          )}
          <div ref={containerRef} className="absolute inset-0" data-testid="jitsi-container" />
          {/* Overlay sponsor pill — floats top-left of the Jitsi frame */}
          <div className="absolute top-3 left-3 z-20 pointer-events-auto">
            <AdSlot location="live_room_sponsor" variant="overlay-pill" />
          </div>
        </div>

        {/* Right column: tabs + sponsor ad */}
        <aside className="flex flex-col gap-3 min-w-0">
          <div className="rounded-2xl border border-border bg-card flex flex-col overflow-hidden flex-1 min-h-[420px]">
            <div className="flex items-center gap-1 px-2 pt-2 border-b border-border/60">
              {[
                { k: 'roster', l: 'Roster', icon: <Users className="w-3 h-3" /> },
                { k: 'notes',  l: 'Shared notes', icon: <StickyNote className="w-3 h-3" /> },
              ].map((t) => (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => setRightPanel(t.k)}
                  data-testid={`right-tab-${t.k}`}
                  className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 h-8 rounded-t-lg border-b-2 -mb-px ${
                    rightPanel === t.k
                      ? 'text-primary border-primary bg-primary/5'
                      : 'text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/60'
                  }`}
                >
                  {t.icon} {t.l}
                </button>
              ))}
            </div>

            {rightPanel === 'roster' && (
              <div className="flex-1 overflow-y-auto p-3" data-testid="roster-panel">
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                  {roster.live_count} live · {roster.hands_up} hand{roster.hands_up === 1 ? '' : 's'} up
                </div>
                <ul className="space-y-1">
                  {roster.live.map((r) => (
                    <li key={r.user_id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 bg-muted/40" data-testid={`roster-user-${r.user_id}`}>
                      <div className="h-7 w-7 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 overflow-hidden">
                        {r.user_avatar
                          ? <img src={r.user_avatar} alt="" className="h-full w-full object-cover" />
                          : (r.user_name || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-semibold truncate">{r.user_name}</div>
                        {r.email && <div className="text-[10px] text-muted-foreground truncate">{r.email}</div>}
                      </div>
                      {r.is_host && <ShieldCheck className="w-3 h-3 text-emerald-500 flex-shrink-0" />}
                      {r.hand_raised_at && <Hand className="w-3 h-3 text-amber-500 animate-pulse flex-shrink-0" />}
                    </li>
                  ))}
                  {roster.live.length === 0 && (
                    <li className="text-xs text-muted-foreground italic px-2 py-3">Waiting for attendees…</li>
                  )}
                </ul>
                {roster.past?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">
                      Left ({roster.past.length})
                    </div>
                    <ul className="space-y-1">
                      {roster.past.map((r) => (
                        <li key={r.user_id + (r.left_at || '')} className="flex items-center gap-2 rounded-lg px-2 py-1 text-muted-foreground">
                          <div className="text-[11px] truncate flex-1">{r.user_name}</div>
                          <div className="text-[10px]">{r.left_at ? formatDistanceToNow(parseISO(r.left_at), { addSuffix: true }) : ''}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {rightPanel === 'notes' && (
              <div className="flex-1 flex flex-col p-3" data-testid="notes-panel">
                <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                  <span>Shared notes (auto-saves)</span>
                  <span className="normal-case tracking-normal">
                    {notesSaving
                      ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> saving…</span>
                      : notesUpdatedAt
                        ? `Saved ${formatDistanceToNow(parseISO(notesUpdatedAt), { addSuffix: true })}`
                        : 'Not saved yet'}
                  </span>
                </div>
                <textarea
                  value={notes}
                  onChange={(e) => { setNotes(e.target.value); scheduleNotesSave(e.target.value); }}
                  data-testid="shared-notes-textarea"
                  placeholder="Take collaborative notes… everyone in the room can edit."
                  className="flex-1 rounded-lg border border-border bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 font-mono"
                />
              </div>
            )}
          </div>

          {/* Sponsor / partner card — attention-optimized live room placement */}
          <AdSlot location="live_room_sponsor" variant="sidebar" className="mb-0" />
        </aside>
      </div>

      {/* Bottom banner sponsor — persistent, low-intrusion placement below the call */}
      <div className="max-w-[1600px] w-full mx-auto px-4 pb-4">
        <AdSlot location="live_room_sponsor" variant="bottom-banner" />
      </div>
    </div>
  );
};

export default LiveSessionRoom;
